import React from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { FileMinus, ArrowRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);

const formatPct = (rate) => `${((rate || 0) * 100).toFixed(1)}%`;

const SummaryStat = ({ label, value, accent = 'text-gray-900', testId }) => (
  <div className="bg-gray-50 rounded-lg p-3" data-testid={testId}>
    <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-semibold mt-1 ${accent}`}>{value}</p>
  </div>
);

export const CreditMemoAnalyticsCard = ({ data, loading = false, months = 6 }) => {
  const summary = data?.summary || {};
  const byCategory = data?.by_category || [];
  const monthlyTrend = data?.monthly_trend || [];
  const topClients = data?.top_clients || [];

  const hasAny = !loading && summary.total_count > 0;

  return (
    <Card data-testid="credit-memo-analytics-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileMinus className="h-5 w-5 text-rose-600" />
          Credit Memos — Last {months} Months
        </CardTitle>
        <Link to="/admin/invoices?tab=credit-memos">
          <Button variant="ghost" size="sm" className="text-rose-600" data-testid="view-credit-memos-link">
            View All <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">
            Loading credit memo analytics…
          </div>
        ) : !hasAny ? (
          <div className="h-[200px] flex flex-col items-center justify-center text-gray-400 text-sm">
            <FileMinus className="h-10 w-10 text-gray-300 mb-2" />
            <p>No credit memos in the selected period.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="credit-memo-summary">
              <SummaryStat
                label="Total Credits"
                value={summary.period_credit_count || 0}
                accent="text-rose-600"
                testId="cm-stat-total-credits"
              />
              <SummaryStat
                label="Credit Amount"
                value={formatCurrency(summary.period_total_credit)}
                accent="text-rose-600"
                testId="cm-stat-credit-amount"
              />
              <SummaryStat
                label="Void Rate"
                value={formatPct(summary.void_rate)}
                accent="text-amber-600"
                testId="cm-stat-void-rate"
              />
              <SummaryStat
                label="Pending Refunds"
                value={formatCurrency(summary.pending_refunds_amount)}
                accent="text-orange-600"
                testId="cm-stat-pending-refunds"
              />
            </div>

            {/* By category & monthly trend side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div data-testid="credit-memo-by-category">
                <p className="text-sm font-medium text-gray-700 mb-2">By Category</p>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byCategory} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        width={120}
                      />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'Credit' ? formatCurrency(value) : value
                        }
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Bar dataKey="count" name="Count" fill="#e11d48" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div data-testid="credit-memo-monthly-trend">
                <p className="text-sm font-medium text-gray-700 mb-2">Monthly Trend</p>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'Credit' ? formatCurrency(value) : value
                        }
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        name="Count"
                        stroke="#e11d48"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top clients */}
            {topClients.length > 0 && (
              <div data-testid="credit-memo-top-clients">
                <p className="text-sm font-medium text-gray-700 mb-2">Top Clients (by credit amount)</p>
                <div className="space-y-2">
                  {topClients.map((c) => (
                    <div
                      key={c.client_account_id || c.client_name}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      data-testid={`cm-top-client-${c.client_account_id || 'unknown'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{c.client_name}</p>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {c.count} credit memo{c.count === 1 ? '' : 's'}
                        </Badge>
                      </div>
                      <p className="font-semibold text-rose-600 ml-4">
                        -{formatCurrency(c.total_credit)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CreditMemoAnalyticsCard;
